import { CommonModule } from "@angular/common";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { Order } from "../../../order-detail-view/order-detail-view.component";
import { QPilotCloudApiService } from "../../../services/qpilot-cloud-api.service";
import { BarTagComponent } from "../../bar-tag/bar-tag.component";
import {
  ChangeOrderItemQuantityEvent,
  DeleteOrderItemEvent,
  UpdateOrderItemQuantityEvent,
} from "../../types/events";
import { QPilotScheduledOrderItem } from "../../types/scheduled-order-item";
import { QPilotSiteSettings } from "../../types/site";
import { IBundle, IBundleItemRewardSetting } from "../bundle.interfaces";
import { ScheduledOrderBundleDetailsProductComponent } from "../scheduled-order-bundle-details-product/scheduled-order-bundle-details-product.component";

@Component({
  selector: "lib-bundles-card",
  standalone: true,
  imports: [
    CommonModule,
    ScheduledOrderBundleDetailsProductComponent,
    BarTagComponent,
  ],
  templateUrl: "./bundles-card.component.html",
  styleUrl: "./bundles-card.component.scss",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BundlesCardComponent {
  private _bundle: IBundle;
  private _order: Order;

  @Input() itemsCount: number;
  @Input() isProcessing: boolean;
  @Input() orderVerboseName: string;
  @Input() siteSettings: QPilotSiteSettings;
  @Input() openDialogUpdateBundle: () => void;

  @Output() updateQuantityEvent = new EventEmitter<{
    event: UpdateOrderItemQuantityEvent;
    item: QPilotScheduledOrderItem;
  }>();
  @Output() deleteOrderItemEvent = new EventEmitter<DeleteOrderItemEvent>();
  @Output() editOrderItemEvent = new EventEmitter<QPilotScheduledOrderItem>();
  @Output() changeQuantityEvent =
    new EventEmitter<ChangeOrderItemQuantityEvent>();

  displayItems: QPilotScheduledOrderItem[] = [];

  @Input()
  set bundle(value: IBundle) {
    this._bundle = value;
    this.updateDisplayItems();
    this.updateRewardsSettings();
    this.calculateRewards();
  }
  get bundle(): IBundle {
    return this._bundle;
  }

  @Input()
  set order(value: Order) {
    this._order = value;
    this.updateDisplayItems();
  }
  get order(): Order {
    return this._order;
  }

  protected totalSavingsPercentage: number | null = null;
  totalSavings: number | null = null;
  rewardDiscountPercentage: number | null = null;
  frequencyDiscountPercentage: number | null = null;
  itemCountToActivateReward: number | null = null;
  isFreeShipping: boolean = false;
  constructor(private qpilotCloudApiService: QPilotCloudApiService) {}

  private calculateRewards(): void {
    if (this.bundle && this.bundle.siteId && this.bundle.id) {
      const productIds = this.displayItems.flatMap((item) => {
        const productId = item.product?.productId;
        const quantity = item.quantity || 1; // Default to 1 if quantity is not defined
        if (productId !== undefined) {
          return Array<number>(quantity).fill(productId);
        } else {
          return [] as number[];
        }
      });

      const frequencyOptionsId = this._bundle.frequencyOptions?.find(
        (x) =>
          x.frequency === this._order.frequency &&
          x.type === this._order.frequencyType,
      )?.id;
      const calculateBundleRewardsRequest = {
        productIds,
        frequencyOptionsId,
      };

      this.qpilotCloudApiService
        .calculateBundleRewards(
          this.bundle.siteId,
          this.bundle.id,
          calculateBundleRewardsRequest,
        )
        .subscribe(
          (result: {
            totalSavingsPercentage: number;
            totalSavings: number;
            isFreeShipping: boolean;
          }) => {
            if (result) {
              this.totalSavingsPercentage = result.totalSavingsPercentage;
              this.totalSavings = result.totalSavings;
              this.isFreeShipping = result.isFreeShipping;
            }
          },
        );
    }
  }

  private updateRewardsSettings(): void {
    if (
      this._bundle?.rewardsSettings &&
      this._bundle.rewardsSettings.length > 0
    ) {
      let greatestRewardSetting: IBundleItemRewardSetting | null = null;

      for (const rewardSetting of this._bundle.rewardsSettings) {
        if (
          greatestRewardSetting === null ||
          rewardSetting.itemCountToActivateReward >
            greatestRewardSetting.itemCountToActivateReward
        ) {
          greatestRewardSetting = rewardSetting;
        }
      }

      if (greatestRewardSetting) {
        this.itemCountToActivateReward =
          greatestRewardSetting.itemCountToActivateReward ?? null;
        this.rewardDiscountPercentage =
          greatestRewardSetting.rewardDiscountPercentage ?? null;
      } else {
        this.itemCountToActivateReward = null;
        this.rewardDiscountPercentage = null;
      }
    } else {
      this.itemCountToActivateReward = null;
      this.rewardDiscountPercentage = null;
    }

    if (
      this._bundle?.frequencyOptions &&
      this._bundle.frequencyOptions.length > 0
    ) {
      let maxFrequencyDiscount = 0;

      for (const frequencyOption of this._bundle.frequencyOptions) {
        if (
          frequencyOption.discountPercentage &&
          frequencyOption.discountPercentage > maxFrequencyDiscount
        ) {
          maxFrequencyDiscount = frequencyOption.discountPercentage;
        }
      }

      // If frequency discount is higher than reward discount, use frequency discount
      if (maxFrequencyDiscount) {
        this.frequencyDiscountPercentage = maxFrequencyDiscount;
      }
    }
  }

  private updateDisplayItems(): void {
    this.displayItems = [];
    // Ensure both inputs have been set before proceeding
    if (!this._bundle?.products || !this._order?.scheduledOrderItems) {
      // Optional: Keep a log here if needed for missing initial data
      return;
    }

    // Use string for the map key as orderItem.productId is a string
    const orderItemsMap = new Map<string, QPilotScheduledOrderItem>();
    this._order.scheduledOrderItems.forEach((item) => {
      // Ensure productId is a truthy string before adding to map
      if (typeof item.productId === "string" && item.productId) {
        orderItemsMap.set(item.productId, item);
      }
    });

    this._bundle.products.forEach((bundleProduct) => {
      // Use bundleProduct.externalProductId (string) for the lookup
      const matchingOrderItem = orderItemsMap.get(
        bundleProduct.externalProductId,
      );

      if (matchingOrderItem) {
        // Remove '- Default Title' from productTitle if it exists
        if (matchingOrderItem.product?.title) {
          matchingOrderItem.product.title = matchingOrderItem.product.title
            .replace(/-?\s*default\s*title\s*/gi, "")
            .trim();
        }
        this.displayItems.push(matchingOrderItem);
      }
    });

    // Recalculate total price based on the displayed items
    // this.totalPrice = 0;
    // The child component will emit prices for the items now in displayItems
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onItemPrice(discount: number) {
    // Assuming discount is per item and the child component emits it
    // this.totalPrice += discount;
    // Manual detectChanges might still be needed depending on ChangeDetectionStrategy
    // and how/when onItemPrice is called by the child. Keeping it for safety.
    // this.changeDetectorRef.detectChanges();
  }
}
